import { Injectable, inject } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpPuebloClient } from '@http/http_pueblo_client';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { LoggingService } from '@services/logging.service';
import {
  PENDING_PUEBLO_RESOURCE_NAME,
  makePuebloResourceName,
} from 'pueblo-types';
import {
  EMPTY,
  catchError,
  concatMap,
  map,
  mergeMap,
  of,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { selectAppUser } from '../user/user-selectors';
import * as actions from './pueblo-actions';
import { selectPueblo, selectPueblosLiveReloading } from './pueblo-selectors';

@Injectable()
export class PuebloEffects {
  private readonly actions$ = inject(Actions);
  private readonly puebloClient = inject(HttpPuebloClient);
  private readonly store = inject(Store);
  private readonly logger = inject(LoggingService);
  private readonly snackBar = inject(MatSnackBar);

  readonly fetchPueblo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.fetchPueblo),
      tap(({ resourceName }) => {
        this.logger.log3('[Effect] Fetching pueblo', resourceName);
      }),
      withLatestFrom(this.store.select(selectPueblosLiveReloading)),
      mergeMap(([{ resourceName }, pueblosLiveReloading]) => {
        if (pueblosLiveReloading.has(resourceName)) return EMPTY;
        pueblosLiveReloading.add(resourceName);
        return this.puebloClient.getPueblo(resourceName).pipe(
          map((pueblo) => actions.fetchPuebloSuccess({ pueblo })),
          tap(() => {
            this.logger.log3('[Effect] Fetched pueblo', resourceName);
          }),
          catchError((message) => {
            this.logger.error(message);
            return of(actions.fetchPuebloFail({ resourceName, message }));
          }),
        );
      }),
    );
  });

  readonly batchFetchPueblos$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.batchFetchPueblos),
      tap(({ resourceNames }) => {
        this.logger.log3(`[Effect] Batch Fetching pueblo`, resourceNames);
      }),
      withLatestFrom(this.store.select(selectPueblosLiveReloading)),
      mergeMap(([{ resourceNames }, pueblosLiveReloading]) => {
        const fetchPueblos = resourceNames.filter(
          (r) => !pueblosLiveReloading.has(r),
        );
        if (!fetchPueblos.length) return EMPTY;
        fetchPueblos.forEach((h) => pueblosLiveReloading.add(h));
        return this.puebloClient.batchGetPueblos(fetchPueblos).pipe(
          map((pueblos) => actions.batchFetchPueblosSuccess({ pueblos })),
          tap(() => {
            this.logger.log3('[Effect] Batch Fetched pueblos');
          }),
          catchError((message) => {
            this.logger.error(message);
            return of(actions.batchFetchPueblosFail({ message }));
          }),
        );
      }),
    );
  });

  readonly createPueblo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.createPueblo),
      tap(({ pueblo }) => {
        this.logger.log3(`[Effect] Create pueblo `, JSON.stringify(pueblo));
      }),
      map(({ pueblo, hooks }) => {
        let puebloWithId = pueblo;
        if (puebloWithId.resourceName === PENDING_PUEBLO_RESOURCE_NAME) {
          puebloWithId = {
            ...pueblo,
            resourceName: makePuebloResourceName(
              this.puebloClient.createPuebloId(),
            ),
          };
        }
        hooks?.onPending?.(puebloWithId);
        return actions.createPuebloWithId({
          pueblo: puebloWithId,
          hooks: hooks,
        });
      }),
    );
  });

  readonly createPuebloWithId$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.createPuebloWithId),
      tap(({ pueblo }) => {
        this.logger.log3(
          `[Effect] Create pueblo (with id) `,
          JSON.stringify(pueblo),
        );
      }),
      withLatestFrom(this.store.select(selectAppUser)),
      concatMap(([{ pueblo, hooks: callback }, appUser]) => {
        return this.puebloClient.createPueblo(pueblo).pipe(
          tap((pueblo) => {
            this.logger.log3(
              `[Effect] Created pueblo `,
              JSON.stringify(pueblo),
            );
            callback?.onSuccess?.(pueblo);
          }),
          map((pueblo) => actions.createPuebloSuccess({ pueblo })),
          catchError((message) => {
            this.logger.error(message);
            callback?.onFailure?.(message);
            return of(
              actions.createPuebloFail({
                message,
                originalPueblo: pueblo,
                originalAppUser: appUser,
              }),
            );
          }),
        );
      }),
    );
  });

  readonly createPuebloSuccess$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.createPuebloSuccess),
      map(() => actions.fetchNextPuebloShortUrl()),
    );
  });

  readonly updatePueblo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.updatePueblo),
      tap(({ pueblo }) => {
        this.logger.log3(`[Effect] Update pueblo`, JSON.stringify(pueblo));
      }),
      concatLatestFrom(({ pueblo }) =>
        this.store.select(selectPueblo(pueblo.resourceName)),
      ),
      map(([{ pueblo, hooks }, originalPueblo]) => {
        if (!originalPueblo) {
          this.logger.error(
            `[Effect] Update pueblo failed: Pueblo not found`,
            JSON.stringify(pueblo),
          );
          return actions.updatePuebloFail({
            message: 'Pueblo not found',
            originalPueblo: undefined,
          });
        }
        hooks?.onPending?.(pueblo);
        return actions.updatePendingPueblo({
          pueblo,
          originalPueblo,
          hooks,
        });
      }),
    );
  });

  readonly updatePendingPueblo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.updatePendingPueblo),
      tap(({ pueblo }) => {
        this.logger.log3(
          `[Effect] Update pueblo (pending) `,
          JSON.stringify(pueblo),
        );
      }),
      concatMap(({ pueblo, originalPueblo, hooks }) => {
        return this.puebloClient.updatePueblo(pueblo, originalPueblo).pipe(
          map((pueblo) => actions.updatePuebloSuccess({ pueblo })),
          tap(({ pueblo }) => {
            this.logger.log3(
              `[Effect] Updated pueblo (pending) `,
              JSON.stringify(pueblo),
            );
            hooks?.onSuccess?.(pueblo);
          }),
          catchError((message) => {
            this.logger.error(message);
            hooks?.onFailure?.(message);
            return of(actions.updatePuebloFail({ message, originalPueblo }));
          }),
        );
      }),
    );
  });

  readonly deletePueblo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.deletePueblo),
      tap(({ resourceName }) => {
        this.logger.log3(`[Effect] Delete pueblo`, resourceName);
      }),
      concatLatestFrom(({ resourceName }) =>
        this.store.select(selectPueblo(resourceName)),
      ),
      map(([{ resourceName, hooks }, originalPueblo]) => {
        if (!originalPueblo) {
          this.logger.error(
            `[Effect] Delete pueblo failed: Pueblo not found`,
            resourceName,
          );
          return actions.deletePuebloFail({
            message: 'Pueblo not found',
            originalPueblo: undefined,
          });
        }
        hooks?.onPending?.(resourceName);
        return actions.deletePendingPueblo({
          resourceName,
          originalPueblo,
          hooks,
        });
      }),
    );
  });

  readonly deletePendingPueblo$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.deletePendingPueblo),
      tap(({ resourceName }) => {
        this.logger.log3(`[Effect] Delete pueblo (pending) `, resourceName);
      }),
      concatMap(({ resourceName, originalPueblo, hooks: callback }) => {
        return this.puebloClient.deletePueblo(resourceName).pipe(
          map(() => actions.deletePuebloSuccess({ resourceName })),
          tap(({ resourceName }) => {
            this.logger.log3(
              `[Effect] Deleted pueblo (pending) `,
              resourceName,
            );

            this.snackBar.open('Pueblo deleted successfully');
            callback?.onSuccess?.(resourceName);
          }),
          catchError((message) => {
            this.logger.error(message);
            callback?.onFailure?.(message);
            return of(actions.deletePuebloFail({ message, originalPueblo }));
          }),
        );
      }),
    );
  });

  readonly fetchNextPuebloShortUrl$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(actions.fetchNextPuebloShortUrl),
      switchMap(() => {
        return this.puebloClient.getShortUrl();
      }),
      switchMap((shortUrl) => {
        return of(actions.fetchNextPuebloShortUrlSuccess({ shortUrl }));
      }),
      catchError((message) => {
        this.logger.error(message);
        return of(actions.fetchNextPuebloShortUrlFail({ message }));
      }),
    );
  });

  readonly displayErrors$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          actions.fetchPuebloFail,
          actions.batchFetchPueblosFail,
          actions.updatePuebloFail,
          actions.createPuebloFail,
          actions.deletePuebloFail,
        ),
        mergeMap(({ message }) => {
          // TODO: Return better/human-readable error messages.
          this.snackBar.open(message);
          return EMPTY;
        }),
      );
    },
    { dispatch: false },
  );
}
